import * as THREE from 'three';

export interface ViewerParams {
  buttons?: boolean;
  color?: boolean;
  view?: keyof typeof GLViewer.enums.views;
}

export class GLViewer {
  static readonly enums = {
    movement: { ROTATE: 0, ZOOM: 1, PAN: 2 },
    views: {
      FRONT: 0,
      BACK: 1,
      LEFT: 2,
      RIGHT: 3,
      TOP: 4,
      BOTTOM: 5,
      ISO: 6,
      ISOZ: 7,
    },
  } as const;

  parameters: Required<ViewerParams>;
  dom_element: HTMLElement;
  scene: THREE.Scene;
  camera: THREE.OrthographicCamera;
  renderer!: THREE.WebGLRenderer;
  main_object: THREE.Object3D;
  current_color: boolean;
  current_view: number;
  view_size = 1;
  aspect_ratio!: number;
  width!: number;
  height!: number;
  zoom_speed = 0.05;
  rotation_center: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
  movement_mode: number = GLViewer.enums.movement.ROTATE;
  buttons_container: HTMLDivElement;

  constructor(
    model?: THREE.Object3D | ReturnType<THREE.Object3D['toJSON']> | Imodel,
    params: ViewerParams = {},
    domElement: HTMLElement = document.body,
  ) {
    this.parameters = {
      buttons: true,
      color: true,
      view: 'FRONT',
      ...params,
    };

    this.dom_element = domElement;
    this.scene = new THREE.Scene();

    this.aspect_ratio = 1;
    this.view_size = 1;
    const left = -this.aspect_ratio * this.view_size;
    const right = this.aspect_ratio * this.view_size;
    const top = this.view_size;
    const bottom = -this.view_size;
    const near = 0.01;
    const far = 1000;

    this.updateAspectRatio();
    this.camera = new THREE.OrthographicCamera(left, right, top, bottom, near, far);
    this.camera.position.set(0, 0, this.view_size);

    this.createLights();
    this.createRenderer();

    this.main_object = new THREE.Object3D();
    this.scene.add(this.main_object);

    this.current_color = this.parameters.color;
    this.current_view = GLViewer.enums.views[this.parameters.view] ?? GLViewer.enums.views.FRONT;

    this.buttons_container = document.createElement('div');
    this.buttons_container.id = 'buttons-container';
    this.buttons_container.style.display = 'flex';
    this.buttons_container.style.bottom = '10px';
    this.buttons_container.style.position = 'absolute';
    this.dom_element.appendChild(this.buttons_container);

    window.addEventListener('resize', () => {
      this.updateAspectRatio();
      this.renderer.setSize(this.width, this.height);
      this.updateCameraFrustrum();
    });

    if (
      model &&
      typeof model === 'object' &&
      'metadata' in model &&
      typeof (model as any).metadata?.formatVersion === 'number' && //NOSONAR
      (model as any).metadata.formatVersion === 3 && //NOSONAR
      'vertices' in model &&
      Array.isArray((model as any).vertices) //NOSONAR
    ) {
      this.loadFromLegacyJson(model as Imodel);
    }

    this.initializeControls();
    this.animate();
  }

  loadFromLegacyJson(model: Imodel): void {
    const loader = new LegacyJSONLoader();
    const geometry = loader.parse(model);

    const material = new THREE.MeshPhongMaterial({
      vertexColors: !!geometry.getAttribute('color'),
      side: THREE.DoubleSide,
    });

    const mesh = new THREE.Mesh(geometry, material);
    this.main_object.add(mesh);
    this.applyView(this.current_view);
    this.fit();
  }

  updateAspectRatio(): void {
    const rect = this.dom_element.getBoundingClientRect();
    this.width = rect.width;
    this.height = rect.height;
    this.aspect_ratio = this.width / this.height;
  }

  updateCameraFrustrum(): void {
    this.camera.left = -this.aspect_ratio * this.view_size;
    this.camera.right = this.aspect_ratio * this.view_size;
    this.camera.top = this.view_size;
    this.camera.bottom = -this.view_size;
    this.camera.near = 0;
    this.camera.far = 2 * this.view_size;
    this.camera.position.set(0, 0, this.view_size * 2);
    this.camera.updateProjectionMatrix();
  }

  createLights(): void {
    // ambient light
    const ambient = new THREE.AmbientLight(0xffffff, 0.8);
    this.scene.add(ambient);

    // directional angle light
    const directional = new THREE.DirectionalLight(0xffffff, 0.5);
    directional.position.set(5, 10, 7.5);
    this.scene.add(directional);

    // opposite side light
    const fill = new THREE.DirectionalLight(0xffffff, 0.3);
    fill.position.set(-5, -5, -7.5);
    this.scene.add(fill);
  }

  createRenderer(): void {
    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    this.updateAspectRatio();
    this.renderer.setSize(this.width, this.height);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setClearColor(0, 0);
    this.renderer.shadowMap.enabled = true;
    this.dom_element.appendChild(this.renderer.domElement);
  }

  animate(): void {
    requestAnimationFrame(() => this.animate());
    this.renderer.render(this.scene, this.camera);
  }

  toggleColor(): void {
    this.current_color = !this.current_color;
    this.main_object.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshPhongMaterial) {
        child.material.vertexColors = this.current_color;
        child.material.color.set(this.current_color ? 0x888888 : 0x555555);
        child.material.needsUpdate = true;
      }
    });
  }

  nextMovementMode(): void {
    this.movement_mode = (this.movement_mode + 1) % 3;
  }

  getMovementModeImage(): string {
    switch (this.movement_mode) {
      case GLViewer.enums.movement.ROTATE:
        return `movement/rotate.png`;
      case GLViewer.enums.movement.PAN:
        return `movement/pan.png`;
      case GLViewer.enums.movement.ZOOM:
        return `movement/zoom.png`;
      default:
        return '';
    }
  }

  getColorImage(): string {
    if (this.current_color) {
      return `color/color.png`;
    } else {
      return `color/no_color.png`;
    }
  }

  initializeControls(): void {
    const mouseStart = { x: 0, y: 0 };
    let isDragging = false;

    const getMousePosition = (event: MouseEvent | TouchEvent) => {
      if ('touches' in event && event.touches.length > 0) {
        return { x: event.touches[0].clientX, y: event.touches[0].clientY };
      } else if ('clientX' in event) {
        return { x: event.clientX, y: event.clientY };
      }
      return { x: 0, y: 0 };
    };

    const onMouseDown = (event: MouseEvent | TouchEvent) => {
      event.preventDefault();
      isDragging = true;
      const pos = getMousePosition(event);
      mouseStart.x = pos.x;
      mouseStart.y = pos.y;
    };

    const onMouseMove = (event: MouseEvent | TouchEvent) => {
      if (!isDragging) return;
      event.preventDefault();
      const mouseEnd = getMousePosition(event);

      switch (this.movement_mode) {
        case GLViewer.enums.movement.ROTATE:
          this.rotateMainObject(mouseStart, mouseEnd);
          break;
        case GLViewer.enums.movement.PAN:
          this.translateMainObject(mouseStart, mouseEnd);
          break;
        case GLViewer.enums.movement.ZOOM:
          this.zoomOnce(mouseEnd.y - mouseStart.y);
          break;
      }

      mouseStart.x = mouseEnd.x;
      mouseStart.y = mouseEnd.y;
    };

    const onMouseUp = () => {
      isDragging = false;
    };

    this.renderer.domElement.addEventListener('mousedown', onMouseDown);
    this.renderer.domElement.addEventListener('mousemove', onMouseMove);
    this.renderer.domElement.addEventListener('mouseup', onMouseUp);
    this.renderer.domElement.addEventListener('touchstart', onMouseDown);
    this.renderer.domElement.addEventListener('touchmove', onMouseMove);
    this.renderer.domElement.addEventListener('touchend', onMouseUp);
    this.renderer.domElement.addEventListener('touchcancel', onMouseUp);
  }

  rotateMainObject(start: { x: number; y: number }, end: { x: number; y: number }): void {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const angleX = (dy / this.height) * Math.PI;
    const angleY = (dx / this.width) * Math.PI;
    const rotationMatrix = new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(angleX, angleY, 0, 'XYZ'));
    const center = this.rotation_center.clone();
    const translationMatrix1 = new THREE.Matrix4().makeTranslation(-center.x, -center.y, -center.z);
    const translationMatrix2 = new THREE.Matrix4().makeTranslation(center.x, center.y, center.z);

    this.main_object.applyMatrix4(translationMatrix1);
    this.main_object.applyMatrix4(rotationMatrix);
    this.main_object.applyMatrix4(translationMatrix2);
  }

  translateMainObject(start: { x: number; y: number }, end: { x: number; y: number }): void {
    const dx = (end.x - start.x) * 0.25;
    const dy = (end.y - start.y) * 0.25;
    const translation = new THREE.Vector3(dx, -dy, 0);
    this.main_object.position.add(translation);
  }

  zoomOnce(dy: number, speed: number = this.zoom_speed): void {
    const factor = 1 + dy * speed;
    this.camera.zoom *= factor;
    this.camera.updateProjectionMatrix();
  }

  getImg(view: number): string {
    const views = GLViewer.enums.views;

    switch (view) {
      case views.FRONT:
        return 'front';
      case views.BACK:
        return 'back';
      case views.LEFT:
        return 'left';
      case views.RIGHT:
        return 'right';
      case views.TOP:
        return 'top';
      case views.BOTTOM:
        return 'bottom';
      case views.ISO:
        return 'iso';
      case views.ISOZ:
        return 'isoz';
      default:
        return 'front';
    }
  }

  applyView(view: number): void {
    const rotation = this.main_object.rotation;
    const views = GLViewer.enums.views;

    switch (view) {
      case views.FRONT:
        rotation.set(0, 0, 0);
        break;
      case views.BACK:
        rotation.set(0, Math.PI, 0);
        break;
      case views.LEFT:
        rotation.set(0, Math.PI / 2, 0);
        break;
      case views.RIGHT:
        rotation.set(0, -Math.PI / 2, 0);
        break;
      case views.TOP:
        rotation.set(Math.PI / 2, 0, 0);
        break;
      case views.BOTTOM:
        rotation.set(-Math.PI / 2, 0, 0);
        break;
      case views.ISO:
        rotation.set(Math.PI / 4, -Math.PI / 4, 0);
        break;
      case views.ISOZ:
        rotation.set(-Math.PI / 4, 0, -Math.PI / 4);
        break;
    }

    if (view >= views.FRONT && view <= views.ISOZ) {
      this.fit();
    }
  }

  fit(): void {
    const box = new THREE.Box3().setFromObject(this.main_object);
    const size = new THREE.Vector3();
    box.getSize(size);
    const center = new THREE.Vector3();
    box.getCenter(center);
    const maxDim = Math.max(size.x, size.y, size.z);
    this.view_size = maxDim * 0.5; // zoom in little
    this.aspect_ratio = this.renderer.domElement.width / this.renderer.domElement.height;
    const left = -this.aspect_ratio * this.view_size;
    const right = this.aspect_ratio * this.view_size;
    const top = this.view_size;
    const bottom = -this.view_size;
    const near = 0.01;
    const far = this.view_size * 10;
    this.camera.left = left;
    this.camera.right = right;
    this.camera.top = top;
    this.camera.bottom = bottom;
    this.camera.near = near;
    this.camera.far = far;
    this.camera.position.set(center.x, center.y, center.z + this.view_size * 2);
    this.camera.lookAt(center);
    this.camera.updateProjectionMatrix();
  }
}

interface Imodel {
  vertices?: number[];
  faces?: number[];
  colors?: number[];
  normals?: number[];
}

export class LegacyJSONLoader {
  constructor(private readonly scale = 1) {}

  parse(model: Imodel): THREE.BufferGeometry {
    const vertices: number[] = model.vertices || [];
    const faces: number[] = model.faces || [];
    const colors: number[] = model.colors || [];
    const normals: number[] = model.normals || [];
    const positionArray: number[] = [];
    const colorArray: number[] = [];
    const normalArray: number[] = [];
    this.processFaces(faces, colors, normals, normalArray, vertices, positionArray, colorArray);

    if (positionArray.some((n) => Number.isNaN(n))) {
      throw new Error('Invalid geometry: position array contains NaN values.');
    }

    if (positionArray.length % 3 !== 0) {
      throw new Error('Invalid geometry: incomplete vertex data.');
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positionArray, 3));

    if (colorArray.length > 0) {
      geometry.setAttribute('color', new THREE.Float32BufferAttribute(colorArray, 3));
    }

    if (normalArray.length > 0) {
      geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normalArray, 3));
    } else {
      geometry.computeVertexNormals();
    }

    geometry.computeBoundingBox();
    return geometry;
  }

  private processFaces(
    faces: number[],
    colors: number[],
    normals: number[],
    normalArray: number[],
    vertices: number[],
    positionArray: number[],
    colorArray: number[],
  ) {
    const isBitSet = (value: number, bit: number): boolean => (value & (1 << bit)) !== 0;

    let i = 0;
    while (i < faces.length) {
      const type = faces[i];
      i++;
      const isQuad = isBitSet(type, 0);
      const hasMaterial = isBitSet(type, 1);
      const hasFaceVertexUv = isBitSet(type, 3);
      const hasFaceNormal = isBitSet(type, 4);
      const hasFaceVertexNormal = isBitSet(type, 5);
      const hasFaceColor = isBitSet(type, 6);
      const hasFaceVertexColor = isBitSet(type, 7);

      if (isQuad) {
        const a = faces[i];
        i++;
        const b = faces[i];
        i++;
        const c = faces[i];
        i++;
        const d = faces[i];
        i++;

        if (hasMaterial) {
          i++;
        }
        if (hasFaceVertexUv) {
          i += 8;
        }

        let faceColor: THREE.Color | null = null;
        const vertexColorsArr: number[][] = [];
        const resultFace = this.processFaceColor(hasFaceColor, faces, i, colors, faceColor);
        i = resultFace.i;
        faceColor = resultFace.faceColor;
        i = this.processFaceVertexColor(hasFaceVertexColor, faces, i, colors, vertexColorsArr);
        i = this.processFaceNormal(hasFaceNormal, faces, i, normals, normalArray, 6);
        i = this.processFaceVertexNormal(hasFaceVertexNormal, faces, i, normals, normalArray);
        this.processQuad(vertices, { a, b, c, d }, vertexColorsArr, faceColor, positionArray, colorArray);
      }
      const index1 = faces[i];
      i++;
      const index2 = faces[i];
      i++;
      const index3 = faces[i];
      i++;
      const indices: number[] = [index1, index2, index3];
      if (hasMaterial) {
        i++;
      }
      if (hasFaceVertexUv) {
        i += 6;
      }
      i = this.processFaceNormal(hasFaceNormal, faces, i, normals, normalArray, 3);
      i = this.processFaceVertexNormal2(hasFaceVertexNormal, faces, i, normals, normalArray);
      let faceColorVertex: THREE.Color | null = null;
      const vertexColors: number[][] = [];
      const resultFace2 = this.processFaceColor(hasFaceColor, faces, i, colors, faceColorVertex);
      i = resultFace2.i;
      faceColorVertex = resultFace2.faceColor;
      i = this.processFaceVertexColor(hasFaceVertexColor, faces, i, colors, vertexColors);
      const faceIndices: number[] = [];
      const faceColors: number[][] = [];
      let valid = true;
      valid = this.processFaceIndices(indices, vertices, valid, faceIndices, vertexColors, faceColors, faceColorVertex);
      this.processFaceIndices9(valid, faceIndices, faceColors, positionArray, colorArray);
    }
  }

  private processFaceIndices9(
    valid: boolean,
    faceIndices: number[],
    faceColors: number[][],
    positionArray: number[],
    colorArray: number[],
  ) {
    if (valid && faceIndices.length === 9 && faceColors.length === 3) {
      positionArray.push(...faceIndices);
      faceColors.forEach((col) => colorArray.push(...col));
    }
  }

  private processFaceVertexNormal2(
    hasFaceVertexNormal: boolean,
    faces: number[],
    i: number,
    normals: number[],
    normalArray: number[],
  ) {
    if (hasFaceVertexNormal) {
      for (let j = 0; j < 3; j++) {
        const normalIndex = faces[i] * 3;
        i++;
        const nx = normals[normalIndex];
        const ny = normals[normalIndex + 1];
        const nz = normals[normalIndex + 2];
        normalArray.push(nx, ny, nz);
      }
    }
    return i;
  }

  private processQuad(
    vertices: number[],
    quad: {
      a: number;
      b: number;
      c: number;
      d: number;
    },
    vertexColors: number[][],
    faceColor: THREE.Color | null,
    positionArray: number[],
    colorArray: number[],
  ) {
    const { a, b, c, d } = quad;
    const quads = [
      [a, b, d],
      [b, c, d],
    ];
    for (const q of quads) {
      const tri = q;
      const faceIndices: number[] = [];
      const faceColors: number[][] = [];
      let valid = true;
      valid = this.processScale(
        tri,
        vertices,
        valid,
        faceIndices,
        { a, b, c, d },
        { vertexColors, faceColors, faceColor },
      );
      if (valid && faceIndices.length === 9 && faceColors.length === 3) {
        positionArray.push(...faceIndices);
        faceColors.forEach((cl) => colorArray.push(...cl));
      }
    }
  }

  private processScale(
    tri: number[],
    vertices: number[],
    valid: boolean,
    faceIndices: number[],
    quad: {
      a: number;
      b: number;
      c: number;
      d: number;
    },
    colors: {
      vertexColors: number[][];
      faceColors: number[][];
      faceColor: THREE.Color | null;
    },
  ) {
    const { vertexColors, faceColors, faceColor } = colors;
    const { a, b, c, d } = quad;
    for (let j = 0; j < 3; j++) {
      const index = tri[j];
      const vi = index * 3;
      const resultValidation = this.validateVertices(vi, vertices, valid);
      valid = resultValidation.valid;
      const { vx, vy, vz } = resultValidation;
      if (!valid) {
        break;
      }
      faceIndices.push(vx, vy, vz);

      let cIndex = -1;
      if (index === a) cIndex = 0;
      else if (index === b) cIndex = 1;
      else if (index === c) cIndex = 2;
      else if (index === d) cIndex = 3;

      if (cIndex >= 0 && vertexColors.length === 4) {
        faceColors.push(vertexColors[cIndex]);
      } else if (faceColor) {
        faceColors.push([faceColor.r, faceColor.g, faceColor.b]);
      }
    }
    return valid;
  }

  private processFaceIndices(
    indices: number[],
    vertices: number[],
    valid: boolean,
    faceIndices: number[],
    vertexColors: number[][],
    faceColors: number[][],
    faceColor: THREE.Color | null,
  ) {
    for (let j = 0; j < 3; j++) {
      const index = indices[j];
      const vi = index * 3;
      const resultValidation = this.validateVertices(vi, vertices, valid);
      valid = resultValidation.valid;
      if (!valid) {
        break;
      }
      const { vx, vy, vz } = resultValidation;
      faceIndices.push(vx, vy, vz);

      if (vertexColors.length === 3) {
        faceColors.push(vertexColors[j]);
      } else if (faceColor) {
        faceColors.push([faceColor.r, faceColor.g, faceColor.b]);
      }
    }
    return valid;
  }

  private validateVertices(vi: number, vertices: number[], valid: boolean) {
    if (vi + 2 >= vertices.length) {
      valid = false;
    }
    const vx = vertices[vi] * this.scale;
    const vy = vertices[vi + 1] * this.scale;
    const vz = vertices[vi + 2] * this.scale;
    if ([vx, vy, vz].some((n) => Number.isNaN(n))) {
      valid = false;
    }
    return { valid, vx, vy, vz };
  }

  private processFaceVertexNormal(
    hasFaceVertexNormal: boolean,
    faces: number[],
    i: number,
    normals: number[],
    normalArray: number[],
  ) {
    if (hasFaceVertexNormal) {
      for (let j = 0; j < 4; j++) {
        const normalIndex = faces[i] * 3;
        i++;
        const nx = normals[normalIndex];
        const ny = normals[normalIndex + 1];
        const nz = normals[normalIndex + 2];

        if (j !== 2) normalArray.push(nx, ny, nz);
        if (j !== 0) normalArray.push(nx, ny, nz);
      }
    }
    return i;
  }

  private processFaceNormal(
    hasFaceNormal: boolean,
    faces: number[],
    i: number,
    normals: number[],
    normalArray: number[],
    max: number,
  ) {
    if (hasFaceNormal) {
      const normalIndex = faces[i] * 3;
      i++;
      const nx = normals[normalIndex];
      const ny = normals[normalIndex + 1];
      const nz = normals[normalIndex + 2];
      for (let j = 0; j < max; j++) normalArray.push(nx, ny, nz);
    }
    return i;
  }

  private processFaceVertexColor(
    hasFaceVertexColor: boolean,
    faces: number[],
    i: number,
    colors: number[],
    vertexColors: number[][],
  ) {
    if (hasFaceVertexColor) {
      for (let j = 0; j < 4; j++) {
        const colorIndex = faces[i];
        i++;
        if (typeof colorIndex !== 'number' || colorIndex < 0 || colorIndex >= colors.length) {
          vertexColors.push([0.5, 0.5, 0.5]);
        } else {
          const hex = colors[colorIndex];
          const c = new THREE.Color(hex);
          vertexColors.push([c.r, c.g, c.b]);
        }
      }
    }
    return i;
  }

  private processFaceColor(
    hasFaceColor: boolean,
    faces: number[],
    i: number,
    colors: number[],
    faceColor: THREE.Color | null,
  ) {
    if (hasFaceColor) {
      const colorIndex = faces[i];
      i++;
      if (typeof colorIndex !== 'number' || colorIndex < 0 || colorIndex >= colors.length) {
        faceColor = new THREE.Color(0x808080);
      } else {
        const hex = colors[colorIndex];
        faceColor = new THREE.Color(hex);
      }
    }
    return { i, faceColor };
  }
}
